import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  DestroyRef,
  Injector,
  OnInit,
  afterNextRender,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { finalize, map } from 'rxjs';
import { VariavelValorMinimo } from '../../../../core/enum/parametros/variavel-valor-minimo.enum';
import { RegraValorMinimoSaldo } from '../../../../core/models/parametros/beneficios.model';
import { BeneficiosService } from '../../../../core/services/parametros/beneficios.service';
import { ToastService } from '../../../../core/services/toast.service';
import {
  VARIAVEIS_VALOR_MINIMO_OPCOES,
  descreverRegraValorMinimoSaldo,
} from '../../../../core/utils/parametros/beneficios.defaults';
import { MascaraMoedaDirective } from '../../../../shared/directives/mascara-moeda/mascara-moeda.directive';
import { VivestModalAreaVisivelDirective } from '../../../../shared/directives/modal-vivest/vivest-modal-area-visivel.directive';
import { VivestSelectValueAccessorDirective } from '../../../../shared/directives/select-vivest/vivest-select-value-accessor.directive';
import { VivestSwitchValueAccessorDirective } from '../../../../shared/directives/switch-vivest/vivest-switch-value-accessor.directive';
import { LoaderService } from '../../../../shared/services/loader/loader.service';

/** Erro da variável da regra variável (o vivest-select devolve '' quando nada foi escolhido). */
function erroVariavel({ habilitada, tipo, variavel }: RegraValorMinimoSaldo): string {
  return habilitada && tipo === 'variavel' && !variavel ? 'Selecione a variável da regra.' : '';
}

/** Erro do valor fixo ou do multiplicador; só a forma escolhida é validada e, desligada, nada é exigido. */
function erroValorOuMultiplicador({ habilitada, tipo, valor, multiplicador }: RegraValorMinimoSaldo): string {
  if (!habilitada) {
    return '';
  }
  if (tipo === 'fixo') {
    return valor !== null && valor > 0 ? '' : 'Informe o valor mínimo de recebimento, maior que R$ 0,00.';
  }
  return multiplicador !== null && multiplicador > 0 && /^\d+(\.\d{1,2})?$/.test(String(multiplicador))
    ? ''
    : 'Informe um multiplicador maior que 0, com até duas casas decimais.';
}

/** Seção "Regra valor mínimo do saldo": o mínimo do recebimento por valor fixo ou regra variável, para o plano. */
@Component({
  selector: 'app-beneficios-valor-minimo-saldo',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MascaraMoedaDirective,
    VivestSelectValueAccessorDirective,
    VivestSwitchValueAccessorDirective,
    VivestModalAreaVisivelDirective,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './beneficios-valor-minimo-saldo.component.html',
  styleUrl: './beneficios-valor-minimo-saldo.component.scss',
})
export class BeneficiosValorMinimoSaldoComponent implements OnInit {
  private readonly fb = inject(FormBuilder).nonNullable;
  private readonly beneficiosService = inject(BeneficiosService);
  private readonly loaderService = inject(LoaderService);
  private readonly toastService = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);

  readonly planoId = input.required<number>();
  readonly nomePlano = input('');

  readonly variaveis = VARIAVEIS_VALOR_MINIMO_OPCOES;
  readonly tipos: readonly { value: RegraValorMinimoSaldo['tipo']; label: string }[] = [
    { value: 'fixo', label: 'Valor fixo' },
    { value: 'variavel', label: 'Regra variável' },
  ];

  /** Último estado salvo, para o Cancelar e para saber se há alterações. */
  private readonly carregado = signal<RegraValorMinimoSaldo | null>(null);
  readonly carregou = computed(() => this.carregado() !== null);

  readonly confirmandoSalvar = signal(false);
  readonly salvando = signal(false);
  readonly tentouSalvar = signal(false);

  readonly form = this.fb.group({
    habilitada: false,
    tipo: 'fixo' as RegraValorMinimoSaldo['tipo'],
    valor: this.fb.control<number | null>(null),
    variavel: this.fb.control<VariavelValorMinimo | null>(null),
    multiplicador: this.fb.control<number | null>(null),
  });

  readonly valor = toSignal(this.form.valueChanges.pipe(map(() => this.form.getRawValue())), {
    initialValue: this.form.getRawValue(),
  });

  readonly temAlteracoes = computed(() => {
    const carregado = this.carregado();
    return !!carregado && JSON.stringify(this.valor()) !== JSON.stringify(carregado) && !this.salvando();
  });

  readonly invalido = computed(() => !!erroVariavel(this.valor()) || !!erroValorOuMultiplicador(this.valor()));
  readonly erroVariavel = computed(() => (this.tentouSalvar() ? erroVariavel(this.valor()) : ''));
  readonly erroValorOuMultiplicador = computed(() =>
    this.tentouSalvar() ? erroValorOuMultiplicador(this.valor()) : '',
  );
  /** Campo fixo com erro (só no tipo "fixo"). */
  readonly erro = computed(() => (this.valor().tipo === 'fixo' ? this.erroValorOuMultiplicador() : ''));
  readonly erroMultiplicador = computed(() => (this.valor().tipo === 'variavel' ? this.erroValorOuMultiplicador() : ''));
  readonly resumo = computed(() => descreverRegraValorMinimoSaldo(this.valor()));

  ngOnInit(): void {
    this.loaderService.showLoader();
    this.beneficiosService
      .obterRegraValorMinimoSaldo(this.planoId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (regra) => {
          this.aplicar(regra);
          afterNextRender(() => this.loaderService.hideLoader(), { injector: this.injector });
        },
        error: () => {
          this.loaderService.hideLoader();
          this.toastService.show('Não foi possível carregar a regra de valor mínimo do saldo.', 'error');
        },
      });
  }

  salvar(): void {
    this.tentouSalvar.set(true);
    if (!this.temAlteracoes()) {
      return;
    }
    if (this.invalido()) {
      this.toastService.show('Revise a regra de valor mínimo do saldo antes de salvar.', 'error');
      return;
    }
    this.confirmandoSalvar.set(true);
  }

  confirmarSalvar(): void {
    this.confirmandoSalvar.set(false);
    if (this.invalido() || this.salvando()) {
      return;
    }
    this.salvando.set(true);
    this.loaderService.showLoader();
    this.beneficiosService
      .salvarRegraValorMinimoSaldo(this.planoId(), { ...this.form.getRawValue(), variavel: this.form.getRawValue().variavel || null })
      .pipe(
        finalize(() => {
          this.salvando.set(false);
          this.loaderService.hideLoader();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (salva) => {
          this.aplicar(salva);
          this.toastService.show('Regra de valor mínimo do saldo salva.', 'success');
        },
        error: () => this.toastService.show('Não foi possível salvar a regra de valor mínimo do saldo.', 'error'),
      });
  }

  descartar(): void {
    const carregado = this.carregado();
    if (carregado) {
      this.aplicar(carregado);
    }
  }

  private aplicar(regra: RegraValorMinimoSaldo): void {
    this.form.reset(structuredClone(regra));
    this.carregado.set(structuredClone(regra));
    this.tentouSalvar.set(false);
  }
}
